import shp from "shpjs";
import JSZip from "jszip";
import rewind from "@turf/rewind";
import {
  Patch,
  Tree,
  RevegetationArea,
  featureSource,
  treeType,
  treeCmType,
  treeSize,
  nvrTools,
  nvrToolModes,
  featureTypeFieldValues,
  validations,
  featureTypes,
  validationFeatureCreationContext,
  ValidationResult,
} from "../lib/types";
import {
  doesGeometryOverlapGeometries,
  getArea,
  getAreaToPerimeterRatio,
  getLTBenchmarks,
  getOverlappingFeatures,
  projectGeometries,
} from "./featureUtils";
import Polygon from "@arcgis/core/geometry/Polygon";
import Point from "@arcgis/core/geometry/Point";
import Multipoint from "@arcgis/core/geometry/Multipoint";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import Map from "@arcgis/core/Map";
import mapConfig from "../lib/mapConfig";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine.js";
import { runValidations } from "./validations";

export interface UploadResults {
  patches?: Patch[];
  trees?: Tree[];
  revegetationAreas?: RevegetationArea[];
  errors?: {
    criticalErrors?: string[];
    nonCriticalErrors?: string[];
  };
}

const requiredFileTypes = [".shp", ".shx", ".dbf", ".prj"];
const acceptedProjections = [
  "GDA2020", // ESRI
  "GCS_GDA2020", // QGIS
  "GDA2020_MGA_Zone_54",
  "GDA2020_MGA_Zone_55",
  "GDA2020_Vicgrid",
];
const acceptedSrids = ["7844", "7854", "7855", "7899"];
const invalidCmValues = [null, "", "0", 0];

const validateSingleFileType = (fileNames: string[], fileType: string) => {
  return (
    fileNames.filter((fileName: string) => fileName.endsWith(fileType))
      .length === 1
  );
};

export const validateZipFileContents = (file: any) => {
  return new Promise((resolve, reject) => {
    JSZip.loadAsync(file).then(async (zip: any) => {
      const fileNames = Object.keys(zip.files);
      const macosxFiles = fileNames.filter((fileName) => {
        return fileName.startsWith("__MACOSX");
      });
      if (macosxFiles.length > 0) {
        macosxFiles.forEach((macosxFile) => {
          delete zip.files[macosxFile];
        });
      }
      const remainingFileNames = Object.keys(zip.files);
      const invalidContents = requiredFileTypes.some(
        (x) => !validateSingleFileType(remainingFileNames, x)
      );
      if (invalidContents) {
        reject(`Invalid zip file contents.
        Please ensure exactly one of the following file types is included in the zip file: ${requiredFileTypes.join(
          ", "
        )}.`);
      }

      if (!invalidContents) {
        const projFile = remainingFileNames.filter((x) =>
          x.toLowerCase().endsWith("prj")
        )[0];
        const projectionString = await zip.files[projFile].async("text");
        const validProjection = acceptedProjections.some((x) =>
          projectionString.includes(x)
        );
        if (!validProjection) {
          reject(`Invalid projection.
          Please ensure that the shapefiles are in the GDA 2020 datum (accepted SRIDs: ${acceptedSrids.join(
            ", "
          )}).`);
        }
      }
      resolve(true);
    });
  });
};

export const readAndConvertShapefile = (file: any) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const buffer: any = reader.result;
      shp(buffer).then(
        (geojson: any) => {
          if (Array.isArray(geojson)) {
            reject("Multiple files in uploaded zip file");
          } else {
            resolve(rewind(geojson));
          }
        },
        (reason: any) => {
          reject(reason);
        }
      );
    };

    reader.onabort = () => {
      reject("File reading aborted: " + file.name);
    };

    reader.onerror = () => {
      reject("File reading error: " + file.name);
    };

    reader.readAsArrayBuffer(file);
  });
};

const separateFeatures = (fcs: any[], nvrToolMode: nvrToolModes) => {
  const patchesJson: any[] = [];
  const treesJson: any[] = [];
  const revegetationAreasJson: any[] = [];
  const multiPolygons: any[] = [];

  for (let fc of fcs) {
    for (let f of fc.features) {
      if (f.geometry.type === "Point") {
        treesJson.push(f);
      } else if (f.geometry.type === "MultiPolygon") {
        multiPolygons.push(f);
      } else if (
        f.geometry.type === "Polygon" &&
        f.properties.type === featureTypeFieldValues.revegetationArea &&
        nvrToolMode === nvrTools.offset
      ) {
        revegetationAreasJson.push(f);
      } else if (f.geometry.type === "Polygon") {
        patchesJson.push(f);
      }
    }
  }
  return {
    patchesJson,
    treesJson,
    revegetationAreasJson,
    multiPolygons,
  };
};

const findTreeCmType = (jsonProperties: any): treeCmType | undefined => {
  return jsonProperties.meas_type.toLowerCase() === "cir"
    ? treeCmType.CIR
    : treeCmType.DBH;
};

const areGeometriesDistinct = (geoms: any[]) => {
  return geoms.every((x: any, index: number) => {
    const restGeoms = geoms.slice();
    restGeoms.splice(index, 1);
    return !doesGeometryOverlapGeometries(x, restGeoms);
  });
};

const isCmValueValid = (jsonProperties: any) => {
  if (!["cir", "dbh"].includes(jsonProperties.meas_type?.toLowerCase())) {
    return false;
  }
  return (
    !invalidCmValues.includes(jsonProperties.meas) &&
    Number(jsonProperties.meas) > 0
  );
};

const createPolygonFromJson = (json: any) => {
  const polygon = new Polygon({
    rings: json.geometry.coordinates,
    spatialReference: new SpatialReference({ wkid: 4326 }),
  });
  const simplePolygon = geometryEngine.simplify(polygon);
  return simplePolygon;
};

const createPointFromJson = (json: any) => {
  return new Point({
    longitude: json.geometry.coordinates[0],
    latitude: json.geometry.coordinates[1],
    spatialReference: new SpatialReference({ wkid: 4326 }),
  });
};

const bundleValidations = async (
  validation: validations,
  geoms: Array<Point | Polygon>,
  featureTypes: Array<featureTypes>,
  map: any,
  supportingData?: any
): Promise<ValidationResult> => {
  const result = await Promise.all(
    geoms.map((x, index) =>
      runValidations(
        [validation],
        x,
        featureTypes[index],
        map,
        undefined,
        validationFeatureCreationContext.upload,
        supportingData
      )
    )
  );
  return result.flat().filter((x) => x.fail)[0] || {};
};

const bundleTreesBelowDbhValidations = async (
  trees: Array<Point>,
  treesJson: any[],
  patchGeometries: Array<Polygon>,
  map: Map
): Promise<ValidationResult> => {
  const result = await Promise.all(
    trees.map((x, index) =>
      runValidations(
        [validations.treesBelowDbhThreshold],
        x,
        featureTypes.tree,
        map,
        undefined,
        validationFeatureCreationContext.upload,
        {
          tree: {
            cmType: findTreeCmType(treesJson[index].properties),
            cm: Math.round(Number(treesJson[index].properties["meas"])),
          },
          overlappingPatches: getOverlappingFeatures(
            x,
            patchGeometries.map((x) => ({ geom: x }))
          ),
        }
      )
    )
  );
  return result.flat().filter((x) => x.fail)[0] || {};
};

const validateFeatures = async (
  patchesJson: any[],
  treesJson: any[],
  revegetationAreasJson: any[],
  multiPolygons: any[],
  patchGeometries: any[],
  treeGeometries: any[],
  revegetationAreaGeometries: any[],
  map: Map,
  nvrToolMode: nvrToolModes
): Promise<string[]> => {
  const criticalErrors: string[] = [];

  if (multiPolygons.length > 0) {
    criticalErrors.push("Polygons with multiple parts were found");
  }

  if (
    patchesJson.length + treesJson.length + revegetationAreasJson.length ===
    0
  ) {
    criticalErrors.push("No point or polygon features found");
    return criticalErrors;
  }

  if (!areGeometriesDistinct(patchGeometries)) {
    criticalErrors.push("Overlap between patches found");
  }

  if (!areGeometriesDistinct(revegetationAreaGeometries)) {
    criticalErrors.push("Overlap between revegetation areas found");
  }

  if (
    !areGeometriesDistinct(
      revegetationAreaGeometries.concat(patchGeometries)
    ) &&
    areGeometriesDistinct(revegetationAreaGeometries) &&
    areGeometriesDistinct(patchGeometries)
  ) {
    criticalErrors.push("Overlap between revegetation areas and patches found");
  }

  if (treesJson.some((x: any) => !isCmValueValid(x.properties))) {
    criticalErrors.push("No circumference or DBH value found for some trees");
  }

  const criticalValidationsToRun = [];
  criticalValidationsToRun.push(
    bundleValidations(
      validations.invalidPolygon,
      patchGeometries,
      patchGeometries.map(() => featureTypes.patch),
      map
    )
  );
  criticalValidationsToRun.push(
    bundleValidations(
      validations.invalidPolygon,
      revegetationAreaGeometries,
      revegetationAreaGeometries.map(() => featureTypes.revegetationArea),
      map
    )
  );

  const allPolygonGeoms = patchGeometries.concat(revegetationAreaGeometries);
  if (allPolygonGeoms.length) {
    criticalValidationsToRun.push(
      runValidations(
        [validations.outsideSupportingRasters],
        geometryEngine.union(allPolygonGeoms) as Polygon,
        featureTypes.patch,
        map,
        undefined,
        validationFeatureCreationContext.upload
      )
    );
  }

  if (patchGeometries.length) {
    const unionedPatches = geometryEngine.union(patchGeometries);
    criticalValidationsToRun.push(
      runValidations(
        [validations.msaLevyArea],
        unionedPatches as Polygon,
        featureTypes.patch,
        map,
        undefined,
        validationFeatureCreationContext.upload
      )
    );
  }

  if (treeGeometries.length) {
    const unionedTrees = geometryEngine.union(treeGeometries);
    criticalValidationsToRun.push(
      runValidations(
        [validations.msaLevyArea],
        unionedTrees as Multipoint,
        featureTypes.tree,
        map,
        undefined,
        validationFeatureCreationContext.upload
      )
    );
    criticalValidationsToRun.push(
      runValidations(
        [validations.outsideSupportingRasters],
        unionedTrees as Multipoint,
        featureTypes.tree,
        map,
        undefined,
        validationFeatureCreationContext.upload
      )
    );

    if (
      nvrToolMode === nvrTools.offset &&
      treesJson.every((x: any) => isCmValueValid(x.properties))
    ) {
      criticalValidationsToRun.push(
        bundleTreesBelowDbhValidations(
          treeGeometries,
          treesJson,
          patchGeometries,
          map
        )
      );
    }
  }

  if (revegetationAreaGeometries.length) {
    const unionedRevegetationAreas = geometryEngine.union(
      revegetationAreaGeometries
    );
    if (nvrToolMode === nvrTools.offset) {
      criticalValidationsToRun.push(
        runValidations(
          [
            validations.msaLevyArea,
            validations.wetlands,
            validations.nonWoodyEvc,
          ],
          unionedRevegetationAreas as Polygon,
          featureTypes.revegetationArea,
          map,
          undefined,
          validationFeatureCreationContext.upload
        )
      );
    }
  }

  const results = (await Promise.all(criticalValidationsToRun)).flat();
  const failedResults = results
    .filter((x) => x.fail)
    .map((x) => x.messageContent);

  return criticalErrors.concat(failedResults);
};

const createPatch = async (projectedPatchGeometry: any) => {
  const feature: any = {
    source: featureSource.Uploaded,
    geom: projectedPatchGeometry.toJSON(),
    area: await getArea(projectedPatchGeometry as Polygon),
    type: featureTypeFieldValues.patch,
  };
  return feature;
};

const createRevegArea = async (projectedRevegAreaGeometry: any) => {
  const feature: any = {
    source: featureSource.Uploaded,
    geom: projectedRevegAreaGeometry.toJSON(),
    area: await getArea(projectedRevegAreaGeometry as Polygon),
    type: featureTypeFieldValues.revegetationArea,
    apRatio: await getAreaToPerimeterRatio(
      projectedRevegAreaGeometry as Polygon
    ),
  };
  return feature;
};

const createTree = async (
  projectedTreeGeometry: any,
  properties: any,
  patchGeometries: any[],
  map: Map
) => {
  let vegCode, benchmark, benchmarkCm, size;

  const type = doesGeometryOverlapGeometries(
    projectedTreeGeometry,
    patchGeometries
  )
    ? treeType.Patch
    : treeType.Scattered;
  const cmType = findTreeCmType(properties);
  const cm = Math.round(Number(properties["meas"]));
  const benchmarkData = await getLTBenchmarks(projectedTreeGeometry, map);
  if (benchmarkData) {
    vegCode = benchmarkData.evc;
    const benchmarks = benchmarkData.ltBenchmarks;
    if (benchmarks && benchmarks.length === 1) {
      const tableConfig =
        mapConfig.mapServiceConfig.tables.evcLargeTreeBenchmark;
      benchmark = benchmarks[0][tableConfig.speciesField];
      benchmarkCm = Number(benchmarks[0][tableConfig.benchmarkField]);
      const treeCIR =
        cmType === treeCmType.CIR ? Number(cm) : Number(cm) * Math.PI;
      size = treeCIR > benchmarkCm ? treeSize.Lg : treeSize.Sm;
    }
  }

  const feature = {
    source: featureSource.Uploaded,
    geom: projectedTreeGeometry.toJSON(),
    type,
    cm,
    cmType,
    size,
    vegCode,
    benchmark,
    benchmarkCm,
  };
  return feature;
};

const projectAllGeometries = async (
  patchGeometries: any[],
  treeGeometries: any[],
  revegetationAreaGeometries: any[]
) => {
  const [
    projectedPatchGeometries,
    projectedRevegAreaGeometries,
    projectedTreeGeometries,
  ] = await Promise.all([
    projectGeometries(patchGeometries, 7899),
    projectGeometries(revegetationAreaGeometries, 7899),
    projectGeometries(treeGeometries, 7899),
  ]);

  return {
    projectedPatchGeometries,
    projectedTreeGeometries,
    projectedRevegAreaGeometries,
  };
};

const createFeatures = async (
  projectedPatchGeometries: any[],
  projectedTreeGeometries: any[],
  treesJson: any[],
  projectedRevegAreaGeometries: any[],
  map: Map
) => {
  let patches: any[] = [];
  let trees: any[] = [];
  let revegetationAreas: any[] = [];

  const createPatches: Promise<any>[] = projectedPatchGeometries.map(
    async (x: any) => patches.push(await createPatch(x))
  );
  const createRevegAreas: Promise<any>[] = projectedRevegAreaGeometries.map(
    async (x: any) => revegetationAreas.push(await createRevegArea(x))
  );
  const createTrees: Promise<any>[] = projectedTreeGeometries.map(
    async (x: any, index: number) =>
      trees.push(
        await createTree(
          x,
          treesJson[index].properties,
          projectedPatchGeometries,
          map
        )
      )
  );

  await Promise.all(createPatches.concat(createRevegAreas).concat(createTrees));

  return {
    patches,
    trees,
    revegetationAreas,
  };
};

const checkNonCriticalErrors = async (
  patchGeometries: any[],
  treeGeometries: any[],
  revegetationAreaGeometries: any[],
  map: Map,
  nvrToolMode: nvrToolModes
): Promise<any[]> => {
  const validationsToRun = [];

  if (patchGeometries.length) {
    const unionedPatches = geometryEngine.union(patchGeometries);
    if (nvrToolMode === nvrTools.removal) {
      validationsToRun.push(
        runValidations(
          [validations.msaConservation, validations.msa12Precincts],
          unionedPatches as Polygon,
          featureTypes.patch,
          map,
          undefined,
          validationFeatureCreationContext.upload
        )
      );
    }
    if (nvrToolMode === nvrTools.offset) {
      validationsToRun.push(
        runValidations(
          [validations.wetlands],
          unionedPatches as Polygon,
          featureTypes.patch,
          map,
          undefined,
          validationFeatureCreationContext.upload
        )
      );
    }
  }

  if (treeGeometries.length) {
    const unionedTrees = geometryEngine.union(treeGeometries);
    if (nvrToolMode === nvrTools.removal) {
      validationsToRun.push(
        runValidations(
          [validations.msaConservation, validations.msa12Precincts],
          unionedTrees as Multipoint,
          featureTypes.tree,
          map,
          undefined,
          validationFeatureCreationContext.upload
        )
      );
    }
  }

  if (revegetationAreaGeometries.length) {
    validationsToRun.push(
      bundleValidations(
        validations.apRatio,
        revegetationAreaGeometries,
        revegetationAreaGeometries.map(() => featureTypes.revegetationArea),
        map
      )
    );
  }

  if (nvrToolMode === nvrTools.offset) {
    const allGeoms = patchGeometries
      .concat(revegetationAreaGeometries)
      .concat(treeGeometries);
    let featureTypesArray = patchGeometries
      .concat(revegetationAreaGeometries)
      .map(() => featureTypes.patch); // just needs to be a polygon type
    featureTypesArray = featureTypesArray.concat(
      treeGeometries.map(() => featureTypes.tree)
    );
    validationsToRun.push(
      bundleValidations(
        validations.parcelBoundaryProximity,
        allGeoms,
        featureTypesArray,
        map
      )
    );
  }

  const results = (await Promise.all(validationsToRun)).flat();
  return results.filter((x) => x.fail).map((x) => x.messageContent);
};

export const getUploadResults = async (
  fcs: any[],
  map: Map,
  nvrToolMode: nvrToolModes
): Promise<UploadResults> => {
  const { patchesJson, treesJson, revegetationAreasJson, multiPolygons } =
    separateFeatures(fcs, nvrToolMode);
  const patchGeometries = patchesJson.map((x: any) => createPolygonFromJson(x));
  const treeGeometries = treesJson.map((x: any) => createPointFromJson(x));
  const revegetationAreaGeometries = revegetationAreasJson.map((x: any) =>
    createPolygonFromJson(x)
  );
  const criticalErrors = await validateFeatures(
    patchesJson,
    treesJson,
    revegetationAreasJson,
    multiPolygons,
    patchGeometries,
    treeGeometries,
    revegetationAreaGeometries,
    map,
    nvrToolMode
  );
  if (criticalErrors.length) return { errors: { criticalErrors } };
  const {
    projectedPatchGeometries,
    projectedTreeGeometries,
    projectedRevegAreaGeometries,
  } = await projectAllGeometries(
    patchGeometries,
    treeGeometries,
    revegetationAreaGeometries
  );
  const { patches, trees, revegetationAreas } = await createFeatures(
    projectedPatchGeometries,
    projectedTreeGeometries,
    treesJson,
    projectedRevegAreaGeometries,
    map
  );
  const nonCriticalErrors = await checkNonCriticalErrors(
    projectedPatchGeometries,
    projectedTreeGeometries,
    projectedRevegAreaGeometries,
    map,
    nvrToolMode
  );
  return {
    errors: { criticalErrors: [], nonCriticalErrors },
    patches,
    trees,
    revegetationAreas,
  };
};
